import axiosInstance from '../lib/axios';
import { Role, User } from '../types/models';
import { UserPermissions } from './permission.service';
import { getSecureItem } from '../utils/localStorage';

interface RoleListData {
  data: Role[];
  total: number;
  page: number;
  pageSize: number;
}

interface RoleListResponse {
  statusCode: number;
  message: string;
  data: RoleListData;
}

interface UserListResponse {
  statusCode: number;
  message: string;
  data: {
    data: User[];
    total: number;
    page: number;
    pageSize: number;
  };
}

interface DeleteRoleResponse {
  success: boolean;
  message: string;
  deletedRole?: any;
}

// 🔥 NEW: Permission mapping interface
interface PermissionMapping {
  [key: string]: string; // permission string -> permission ID
}

// 🔥 NEW: Cache for permission mappings
let permissionMappingCache: PermissionMapping | null = null;

// 🔥 NEW: Function to get permission mappings from API
const getPermissionMapping = async (): Promise<PermissionMapping> => {
  if (permissionMappingCache) {
    return permissionMappingCache;
  }

  try {
    // Fetch all permissions from the permissions API
    const response = await axiosInstance.get('/permissions');
    
    
    
    const mapping: PermissionMapping = {};
    
    // Handle the permissions API response structure
    if (response.data && typeof response.data === 'object') {
      if (Object.keys(response.data).length === 0) {
        
        return mapping;
      }
      
      // 🔥 FIX: Handle the actual API response structure
      // Backend returns: { data: [permission objects], total, page, limit, hasMore }
      if (response.data.data && Array.isArray(response.data.data)) {
        
        
        response.data.data.forEach((permission: any, index: any) => {
          if (permission && typeof permission === 'object') {
            // Handle permission object structure: {_id, name, action, description, ...}
            const permissionId = permission._id || permission.id;
            const permissionName = permission.name; // e.g., "candidate.view", "candidate.create"
            
            
            
            if (permissionId && permissionName) {
              // Map permission name directly to ID
              mapping[permissionName] = permissionId;
              
              // Add mapping for candidate permissions to hiring.candidate.*
              const parts = permissionName.split('.');
              if (parts.length === 2) {
                const [module, action] = parts;
                let fullPermissionName = '';
                if (module === 'candidate') {
                  fullPermissionName = `hiring.candidate.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'job') {
                  fullPermissionName = `hiring.job.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'jobPosting') {
                  fullPermissionName = `hiring.jobPosting.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'interview') {
                  fullPermissionName = `hiring.interview.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'employees') {
                  fullPermissionName = `employee_management.employees.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'department') {
                  fullPermissionName = `employee_management.department.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'agreement') {
                  fullPermissionName = `employee_management.agreement.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'compensation') {
                  fullPermissionName = `employee_management.compensation.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'user') {
                  fullPermissionName = `settings.user.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'role') {
                  fullPermissionName = `settings.role.${action}`;
                  mapping[fullPermissionName] = permissionId;
                  // Debug role permissions specifically
                } else if (module === 'system') {
                  fullPermissionName = `system.system.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'status') {
                  fullPermissionName = `hiring.status.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else if (module === 'onboardingTask') {
                  fullPermissionName = `hiring.onboardingTask.${action}`;
                  mapping[fullPermissionName] = permissionId;
                } else {
                  // Fallback: try to guess the module group
                  fullPermissionName = `${module}.${module}.${action}`;
                  mapping[fullPermissionName] = permissionId;
                }
                
                
              }
            } else {
              console.warn(`⚠️ Permission missing required fields:`, {
                id: permissionId,
                name: permissionName,
                fullObject: permission
              });
            }
          } else {
            console.warn(`⚠️ Invalid permission object at index ${index}:`, permission);
          }
        });
      } else {
        Object.entries(response.data).forEach(([module, moduleData]: [string, any]) => {
          if (moduleData && typeof moduleData === 'object') {
            Object.entries(moduleData).forEach(([submodule, permissions]: [string, any]) => {
              if (Array.isArray(permissions)) {
                permissions.forEach((permission: any) => {
                  if (permission.id && permission.action) {
                    const permissionString = `${module}.${submodule}.${permission.action}`;
                    mapping[permissionString] = permission.id;
                  } else if (permission._id && permission.action) {
                    const permissionString = `${module}.${submodule}.${permission.action}`;
                    mapping[permissionString] = permission._id;
                  } else if (permission.id && permission.name) {
                    // Handle alternative naming
                    const permissionString = `${module}.${submodule}.${permission.name}`;
                    mapping[permissionString] = permission.id;
                  } 
                });
              } 
            });
          } 
        });
      }
    }
    
    
    
    // Cache the mapping for future use
    permissionMappingCache = mapping;
    
    return mapping;
  } catch (error) {
    console.error('❌ Error in getPermissionMapping:', error);
    throw new Error('Failed to fetch permission mappings from server');
  }
};

// 🔥 NEW: Function to convert permission strings to IDs
const convertPermissionStringsToIds = async (permissionStrings: string[]): Promise<string[]> => {
  try {
    const mapping = await getPermissionMapping();
    const permissionIds: string[] = [];
    
    
    
    
    permissionStrings.forEach(permissionString => {
      // 🔥 FIX: Try multiple formats for candidate permissions
      let permissionId = mapping[permissionString];
      
      if (!permissionId) {
          // Try alternative formats
          const alternatives = [
            // Try without module prefix
            permissionString.replace('hiring.candidate.', 'candidate.'),
            permissionString.replace('employee_management.employees.', 'employees.'),
            permissionString.replace('employee_management.department.', 'department.'),
            permissionString.replace('employee_management.agreement.', 'agreement.'),
            permissionString.replace('employee_management.compensation.', 'compensation.'),
            permissionString.replace('hiring.job.', 'job.'),
            permissionString.replace('hiring.jobPosting.', 'jobPosting.'),
            permissionString.replace('hiring.interview.', 'interview.'),
            permissionString.replace('hiring.onboarding.', 'onboarding.'),
            permissionString.replace('settings.user.', 'user.'),
            permissionString.replace('settings.role.', 'role.'),
            permissionString.replace('system.system.', 'system.'),
            // Try specific mappings for role.manage
            permissionString.replace('settings.role.manage', 'role.manage'),
            // Try status and onboardingTask permissions
            permissionString.replace('hiring.status.', 'status.'),
            permissionString.replace('hiring.onboardingTask.', 'onboardingTask.'),
          ];
        
        
        
        for (const alt of alternatives) {
          if (mapping[alt]) {
            permissionId = mapping[alt];
            
            break;
          }
        }
      }
      
      if (permissionId) {
        permissionIds.push(permissionId);
        
      } else {
        console.warn(`❌ Permission not found: ${permissionString}`);
        
        // 🔥 DEBUG: Show similar permissions for debugging
        const similarPermissions = Object.keys(mapping).filter(key => 
          key.includes(permissionString.split('.').pop() || '') || 
          permissionString.includes(key.split('.').pop() || '')
        );
        if (similarPermissions.length > 0) {
          console.warn(`🔍 Similar permissions found:`, similarPermissions);
        }
        
        // 🔥 DEBUG: Show role permissions specifically
        if (permissionString.includes('role')) {
          const rolePermissions = Object.keys(mapping).filter(key => key.includes('role'));
          console.warn(`🔍 Role permissions available:`, rolePermissions);
        }
        
        // 🔥 DEBUG: Show interview permissions specifically
        if (permissionString.includes('interview')) {
          const interviewPermissions = Object.keys(mapping).filter(key => key.includes('interview'));
          console.warn(`🔍 Interview permissions available:`, interviewPermissions);
        }
        
        // 🔥 DEBUG: Show status permissions specifically
        if (permissionString.includes('status')) {
          const statusPermissions = Object.keys(mapping).filter(key => key.includes('status'));
          console.warn(`🔍 Status permissions available:`, statusPermissions);
        }
        
        // 🔥 DEBUG: Show onboardingTask permissions specifically
        if (permissionString.includes('onboardingTask')) {
          const onboardingTaskPermissions = Object.keys(mapping).filter(key => key.includes('onboardingTask'));
          console.warn(`🔍 OnboardingTask permissions available:`, onboardingTaskPermissions);
        }
        
        // 🔥 DEBUG: Show onboarding permissions specifically
        if (permissionString.includes('onboarding')) {
          const onboardingPermissions = Object.keys(mapping).filter(key => key.includes('onboarding'));
          console.warn(`🔍 Onboarding permissions available:`, onboardingPermissions);
        }
        
        // 🔥 DEBUG: Show agreement permissions specifically
        if (permissionString.includes('agreement')) {
          const agreementPermissions = Object.keys(mapping).filter(key => key.includes('agreement'));
          console.warn(`🔍 Agreement permissions available:`, agreementPermissions);
        }
        
        // 🔥 DEBUG: Show compensation permissions specifically
        if (permissionString.includes('compensation')) {
          const compensationPermissions = Object.keys(mapping).filter(key => key.includes('compensation'));
          console.warn(`🔍 Compensation permissions available:`, compensationPermissions);
        }
      }
    });
    
    
    return permissionIds;
    
  } catch (error) {
    console.error('❌ Error in convertPermissionStringsToIds:', error);
    throw error;
  }
};

// 🔥 NEW: Function to clear permission mapping cache
const clearPermissionMappingCache = () => {
  permissionMappingCache = null;
};

// 🔥 NEW: Debug function to check permission mappings
const debugPermissionMappings = async () => {
  try {
    const mapping = await getPermissionMapping();
    
    return mapping;
  } catch (error) {
    return {};
  }
};

export const roleService = {
  // 🔥 NEW: Debug function
  debugPermissions: debugPermissionMappings,
  
  // 🔥 NEW: Test function to check permission mapping
  testPermissionMapping: async () => {
    try {
      const mapping = await getPermissionMapping();  
      return mapping;
    } catch (error) {
      console.error('❌ Error testing permission mapping:', error);
      return {};
    }
  },
  
  // Create a new role
  createRole: async (roleData: Partial<Role>) => {
    try {
      // Convert permissions object to permission IDs
      let permissionsArray: string[] = [];
      
      if (roleData.permissions) {
        // Convert permissions object to permission strings first
        Object.entries(roleData.permissions).forEach(([module, modulePerms]: [string, any]) => {
          if (modulePerms && typeof modulePerms === 'object') {
            Object.entries(modulePerms).forEach(([submodule, submodulePerms]: [string, any]) => {
              if (submodulePerms && typeof submodulePerms === 'object') {
                Object.entries(submodulePerms).forEach(([action, hasPermission]: [string, any]) => {
                  if (hasPermission === true) {
                    // For onboarding, backend expects format like "onboarding.create" not "hiring.onboarding.create"
                    let permissionString;
                    if (submodule === 'onboarding') {
                      permissionString = `${submodule}.${action}`;
                    } else if (submodule === 'agreement') {
                      permissionString = `${submodule}.${action}`;
                    } else if (submodule === 'compensation') {
                      permissionString = `${submodule}.${action}`;
                    } else {
                      permissionString = `${module}.${submodule}.${action}`;
                    }
                    permissionsArray.push(permissionString);
                  }
                });
              }
            });
          }
        });
        
        // Try to convert permission strings to IDs, fallback to strings if API fails
        try {
          const permissionIds = await convertPermissionStringsToIds(permissionsArray);
          permissionsArray = permissionIds;
        } catch (error) {
          console.warn('⚠️ Failed to convert permissions to IDs, using permission strings directly:', error);
          // Fallback: send permission strings directly instead of IDs
        }
      }
      
      const backendData = {
        name: roleData.name,
        description: roleData.description || '',
        permissions: permissionsArray // Send actual permissions
      };
      
   const response = await axiosInstance.post<Role>('/roles', backendData);
      const createdRole = response.data;
     
      return createdRole;
    } catch (error: any) {
      console.error('❌ Error creating role:', error);
      throw error;
    }
  },

  // Get all roles with pagination
  getRoles: async (params?: { page?: number; pageSize?: number; search?: string }) => {
    const response = await axiosInstance.get<RoleListResponse>('/roles', { params });
    return response.data;
  },

  // Get a role by ID
  getRoleById: async (id: string) => {
    const response = await axiosInstance.get<Role>(`/roles/${id}`);
    return response.data;
  },

  // Update a role
  updateRole: async (id: string, roleData: Partial<Role>) => {
    // Transform data to backend expected format
    const backendData: any = {};
    
    // Always include required fields
    if (roleData.name) {
      backendData.name = roleData.name;
    }
    
    if (roleData.description !== undefined) {
      backendData.description = roleData.description || '';
    }
    
    // Handle permissions - convert to permission IDs
    if (roleData.permissions) {
      if (Array.isArray(roleData.permissions)) {
        // Check if permissions are already IDs or permission strings
        const firstPermission = roleData.permissions[0];
        const isAlreadyIds = firstPermission && typeof firstPermission === 'string' && 
                           firstPermission.length === 24 && /^[0-9a-fA-F]{24}$/.test(firstPermission);
        
        if (isAlreadyIds) {
          backendData.permissions = roleData.permissions;
        } else {
          try {
            const permissionIds = await convertPermissionStringsToIds(roleData.permissions);
            backendData.permissions = permissionIds;
          } catch (error) {
            console.warn('⚠️ Failed to convert permissions to IDs, using permission strings directly:', error);
            // Fallback: send permission strings directly instead of IDs
            backendData.permissions = roleData.permissions;
          }
        }
      } else {
        // Convert object format to array format first, then to IDs
        const permissionsArray: string[] = [];
        
        Object.entries(roleData.permissions).forEach(([module, modulePerms]: [string, any]) => {
          if (modulePerms && typeof modulePerms === 'object') {
            Object.entries(modulePerms).forEach(([submodule, submodulePerms]: [string, any]) => {
              if (submodulePerms && typeof submodulePerms === 'object') {
              Object.entries(submodulePerms).forEach(([action, hasPermission]: [string, any]) => {
                if (hasPermission === true) {
                  let permissionString;
                  if (submodule === 'onboarding') {
                    permissionString = `${submodule}.${action}`;
                  } else if (submodule === 'agreement') {
                    permissionString = `${submodule}.${action}`;
                  } else if (submodule === 'compensation') {
                    permissionString = `${submodule}.${action}`;
                  } else {
                    permissionString = `${module}.${submodule}.${action}`;
                  }
                  permissionsArray.push(permissionString);
                }
              });
              }
            });
          }
        });
        
        try {
          const permissionIds = await convertPermissionStringsToIds(permissionsArray);
          backendData.permissions = permissionIds;
        } catch (error) {
          console.warn('⚠️ Failed to convert permissions to IDs, using permission strings directly:', error);
          // Fallback: send permission strings directly instead of IDs
          backendData.permissions = permissionsArray;
        }
      }
    }
    
    // Always include isActive field (default to true)
    backendData.isActive = roleData.isActive !== undefined ? roleData.isActive : true;
    
    try {
      const response = await axiosInstance.patch<Role>(`/roles/${id}`, backendData);
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  // Delete a role with improved error handling
  deleteRole: async (id: string): Promise<DeleteRoleResponse> => {
    try {
      const token = getSecureItem('accessToken');
      
      if (!token) {
        throw new Error('Authentication token not found. Please login again.');
      }
      
      if (!id) {
        throw new Error('Role ID is required for deletion.');
      }
      
      const response = await axiosInstance.delete<DeleteRoleResponse>(`/roles/${id}`);
      
      // Ensure the response has the expected structure
      if (response.data && typeof response.data.success === 'boolean') {
        return response.data;
      } else {
        // If the backend doesn't return the expected structure, create one
        return {
          success: true,
          message: 'Role deleted successfully',
          deletedRole: response.data as any
        };
      }
    } catch (error: any) {
      // Handle specific error cases
      if (error.response?.status === 404) {
        throw new Error('Role not found');
      } else if (error.response?.status === 403) {
        throw new Error('You do not have permission to delete this role');
      } else if (error.response?.status === 409) {
        throw new Error('Cannot delete role that is assigned to users');
      } else if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please login again.');
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      } else {
        throw new Error('Failed to delete role. Please try again.');
      }
    }
  },

  // Get all users (for admin)
  getAllUsers: async (params?: { page?: number; pageSize?: number; search?: string }) => {
    const response = await axiosInstance.get<UserListResponse>('/employees', { params });
    return response.data;
  },

  // Get permissions for a specific role
  getRolePermissions: async (roleId: string): Promise<UserPermissions | null> => {
    try {
      const response = await axiosInstance.get<UserPermissions>(`/permissions/role/${roleId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 403) {
        console.warn('⚠️ User does not have permission to view role permissions, trying localStorage fallback');
        
        // Try to get stored permissions from localStorage
        const storedPermissions = roleService.getStoredPermissionsById(roleId);
        if (storedPermissions) {
        
          return storedPermissions;
        }
        
        console.warn('⚠️ No stored permissions found for role:', roleId);
        return null;
      }
      throw error;
    }
  },

  // Update role permissions
  updateRolePermissions: async (roleId: string, permissions: UserPermissions): Promise<Role> => {
    try {
      // Convert permissions object to permission IDs
      const permissionsArray: string[] = [];
      
      Object.entries(permissions).forEach(([module, modulePerms]: [string, any]) => {
        if (modulePerms && typeof modulePerms === 'object') {
          Object.entries(modulePerms).forEach(([submodule, submodulePerms]: [string, any]) => {
            if (submodulePerms && typeof submodulePerms === 'object') {
                Object.entries(submodulePerms).forEach(([action, hasPermission]: [string, any]) => {
                  if (hasPermission === true) {
                    // 🔥 FIX: For onboarding, backend expects format like "onboarding.create" not "hiring.onboarding.create"
                    let permissionString;
                    if (submodule === 'onboarding') {
                      permissionString = `${submodule}.${action}`;
                    } else if (submodule === 'agreement') {
                      permissionString = `${submodule}.${action}`;
                    } else if (submodule === 'compensation') {
                      permissionString = `${submodule}.${action}`;
                    } else {
                      permissionString = `${module}.${submodule}.${action}`;
                    }
                    permissionsArray.push(permissionString);
                  }
                });
            }
          });
        }
      });
      
      // Try to convert permission strings to IDs, fallback to strings if API fails
      let permissionsToSend = permissionsArray;
      try {
        const permissionIds = await convertPermissionStringsToIds(permissionsArray);
        permissionsToSend = permissionIds;
      } catch (error) {
        console.warn('⚠️ Failed to convert permissions to IDs, using permission strings directly:', error);
        // Fallback: send permission strings directly instead of IDs
      }
      
      // Use the general updateRole endpoint with permissions data
      const response = await axiosInstance.patch<Role>(`/roles/${roleId}`, { 
        permissions: permissionsToSend 
      });
      return response.data;
    } catch (error: any) {
      // If the specific permissions endpoint doesn't exist, try the permissions API directly
      if (error.response?.status === 404) {
        try {
          // Alternative: try updating through permissions API
          const permissionsResponse = await axiosInstance.patch<UserPermissions>(`/permissions/role/${roleId}`, permissions);
          
          // Return a role-like object since permissions endpoint might not return full role
          return {
            id: roleId,
            _id: roleId,
            name: 'Updated Role',
            description: '',
            permissions: permissionsResponse.data,
            createdAt: new Date(),
            updatedAt: new Date()
          } as Role;
        } catch (altError: any) {
          throw error; // Throw the original error
        }
      }
      
      throw error;
    }
  },

  // Clear permission mapping cache
  clearPermissionCache: clearPermissionMappingCache,
  
  // Helper function to get stored permissions for a role
  getStoredPermissions: (roleName: string) => {
    const permissionsKey = `role_permissions_${roleName}`;
    const stored = localStorage.getItem(permissionsKey);
    return stored ? JSON.parse(stored) : null;
  },
  
  // Helper function to get stored permissions by role ID
  getStoredPermissionsById: (roleId: string) => {
    const roleIdKey = `role_permissions_${roleId}`;
    const stored = localStorage.getItem(roleIdKey);
    return stored ? JSON.parse(stored) : null;
  },
  
  // Helper function to clear stored permissions
  clearStoredPermissions: (roleName: string) => {
    const permissionsKey = `role_permissions_${roleName}`;
    localStorage.removeItem(permissionsKey);
  },
  
  // Helper function to clear stored permissions by role ID
  clearStoredPermissionsById: (roleId: string) => {
    const roleIdKey = `role_permissions_${roleId}`;
    localStorage.removeItem(roleIdKey);
  },
  
  // Helper function to list all stored permissions
  listStoredPermissions: () => {
    const stored = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('role_permissions_')) {
        const value = localStorage.getItem(key);
        if (value) {
          stored.push({
            key,
            permissions: JSON.parse(value)
          });
        }
      }
    }
    return stored;
  }
};